import graphqlClientFactory from 'lib/graphql-client-factory';
import { ImportedData, ImportedDataType } from './ImportedData.types';
import type { GraphQLResults, IImportedDataRequest } from './ImportedDataRequest.types';

const ITEM_FOLDER_BY_DATA_TYPE: Record<ImportedDataType, string> = {
  [ImportedDataType.SparkleEvent]: 'Sparkle Events',
  [ImportedDataType.SparkleLocation]: 'Sparkle Locations',
  [ImportedDataType.SparkleProvider]: 'Sparkle Providers',
};

const CACHE_KEY_SEGMENT_BY_DATA_TYPE: Record<ImportedDataType, string> = {
  [ImportedDataType.SparkleEvent]: 'sparkle-event',
  [ImportedDataType.SparkleLocation]: 'sparkle-location',
  [ImportedDataType.SparkleProvider]: 'sparkle-provider',
};

function throwUnsupportedImportedDataType(dataType: ImportedDataType): never {
  throw new Error(`Unsupported imported data type: ${dataType}`);
}

export function getItemFolderName(dataType: ImportedDataType): string {
  return ITEM_FOLDER_BY_DATA_TYPE[dataType] ?? throwUnsupportedImportedDataType(dataType);
}

export function getCacheKeySegment(dataType: ImportedDataType): string {
  return CACHE_KEY_SEGMENT_BY_DATA_TYPE[dataType] ?? throwUnsupportedImportedDataType(dataType);
}

export class ImportedDataRequestService {
  private _maxRetries: number;
  constructor(maxRetries: number) {
    this._maxRetries = maxRetries;
  }

  public async getDataByRequest(request: IImportedDataRequest): Promise<ImportedData> {
    if (ImportedDataRequestService.canNotSend(request)) {
      return {} as ImportedData;
    }

    const graphQLClient = graphqlClientFactory({
      retries: this._maxRetries,
    });

    try {
      const result = await graphQLClient.request<GraphQLResults>(request.query, request.variables);

      return ImportedDataRequestService.unpackGraphQLResults(result);
    } catch (error) {
      return {} as ImportedData;
    }
  }

  private static canNotSend(request: IImportedDataRequest): boolean {
    if (!request) return true;
    if (request.isInvalid) return true;
    return false;
  }

  private static unpackGraphQLResults(results: GraphQLResults): ImportedData {
    const data: { [key: string]: string } = {};

    const item = results.item;
    if (!item) return data as ImportedData;

    for (const key in item) {
      if (item.hasOwnProperty(key)) {
        const field = item[key as keyof typeof item];
        if (field && typeof field === 'object' && 'value' in field) {
          data[key] = (field as { value: string }).value;
        }
      }
    }

    return data as unknown as ImportedData;
  }
}
